package com.tovtalk.data.cache

import kotlinx.browser.window
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.await
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine

// ---------------------------------------------------------------------------
// External URL API
// ---------------------------------------------------------------------------

@JsName("URL")
external object JsURL {
    fun createObjectURL(obj: dynamic): String
    fun revokeObjectURL(url: String)
}

// ---------------------------------------------------------------------------
// Cache entry
// ---------------------------------------------------------------------------

private data class CacheEntry(
    val objectUrl: String,
    val sizeBytes: Long,
    var lastAccessTime: Double
)

// ---------------------------------------------------------------------------
// LRU Blob Cache
// ---------------------------------------------------------------------------

/**
 * [isProtected] tells the cache which keys must NOT have their objectURL revoked right now because a
 * caller is actively fetching/awaiting them (e.g. they are in the in-flight map). This is the
 * "do not revoke while in active set" guard that prevents revoking a blob another coroutine is about
 * to hand out. The set is naturally bounded and self-cleaning (it tracks in-flight requests), so it
 * cannot leak the way an unreleased refcount would.
 */
private class LRUBlobCache(
    private val maxSizeBytes: Long,
    private val isProtected: (String) -> Boolean = { false }
) {

    // LinkedHashMap preserves access order (we move-to-end on get) → LRU is at the front.
    private val entries = LinkedHashMap<String, CacheEntry>()
    private var currentSizeBytes: Long = 0L

    fun get(key: String): String? {
        val entry = entries.remove(key) ?: return null
        // Re-insert at end to mark as most-recently-used
        val updated = entry.copy(lastAccessTime = nowMs())
        entries[key] = updated
        return updated.objectUrl
    }

    fun put(key: String, sizeBytes: Long, objectUrl: String) {
        // Remove old entry for same key. Only revoke the old objectURL if it's not currently
        // protected (avoid revoking a blob another caller is mid-fetch / about to receive).
        entries.remove(key)?.let { old ->
            currentSizeBytes -= old.sizeBytes
            if (!isProtected(key)) JsURL.revokeObjectURL(old.objectUrl)
        }
        evictIfNeeded(sizeBytes)
        entries[key] = CacheEntry(objectUrl, sizeBytes, nowMs())
        currentSizeBytes += sizeBytes
    }

    fun remove(key: String) {
        entries.remove(key)?.let { entry ->
            currentSizeBytes -= entry.sizeBytes
            if (!isProtected(key)) JsURL.revokeObjectURL(entry.objectUrl)
        }
    }

    fun clear() {
        entries.values.forEach { JsURL.revokeObjectURL(it.objectUrl) }
        entries.clear()
        currentSizeBytes = 0L
    }

    /**
     * Evict least-recently-used entries until we are back under the cap. Enforces a HARD ceiling:
     * loops until under cap (or nothing safely evictable remains), instead of stopping after the
     * first skipped entry. Entries that are too young (just handed to the DOM) OR currently
     * protected are skipped so a hot/visible blob is never revoked out from under the DOM.
     */
    private fun evictIfNeeded(incomingSize: Long) {
        val now = nowMs()
        val minAgeMs = 10_000.0
        var skipped = 0
        while (currentSizeBytes + incomingSize > maxSizeBytes) {
            val it = entries.entries.iterator()
            // Skip over the leading entries we've already determined we can't evict yet.
            var idx = 0
            var evicted = false
            while (it.hasNext()) {
                val (key, entry) = it.next()
                if (idx < skipped) { idx++; continue }
                idx++
                val tooYoung = now - entry.lastAccessTime < minAgeMs
                if (tooYoung || isProtected(key)) {
                    skipped++
                    continue
                }
                currentSizeBytes -= entry.sizeBytes
                JsURL.revokeObjectURL(entry.objectUrl)
                it.remove()
                evicted = true
                break
            }
            // Nothing evictable remains (all skipped/protected) → stop to avoid an infinite loop.
            if (!evicted) break
        }
    }

    private fun nowMs(): Double = js("Date.now()").unsafeCast<Double>()
}

// ---------------------------------------------------------------------------
// MediaCacheManager – public API
// ---------------------------------------------------------------------------

object MediaCacheManager {

    /** Guards the in-flight dedup map AND cache mutation so a concurrent caller can't revoke an
     *  objectURL another caller is about to hand out. */
    private val mutex = Mutex()

    /** In-flight deduplication: only one fetch per cache-key at a time. */
    private val inFlight = mutableMapOf<String, CompletableDeferred<String>>()

    // A key with an outstanding in-flight fetch/await is "protected": its objectURL must not be
    // revoked by a concurrent put/evict. Always read under [mutex].
    private val imageCache = LRUBlobCache(
        maxSizeBytes = 100L * 1024L * 1024L,
        isProtected = { key -> inFlight.containsKey(key) }
    )

    /**
     * Returns an object URL for the image at [signedUrl].
     * If the URL is not a signed S3 URL the original URL is returned as-is.
     */
    suspend fun getImageUrl(signedUrl: String): String {
        if (!S3CacheKeyResolver.isSignedUrl(signedUrl)) return signedUrl
        return getOrFetchDeduped(S3CacheKeyResolver.resolve(signedUrl), signedUrl, imageCache)
    }

    /**
     * Returns a playable URL for the video at [signedUrl].
     *
     * IMPORTANT: videos are NOT blob-cached. Pulling the full file via fetch().blob() would load the
     * entire progressive MP4 into memory before playback can start, and would turn an HLS `.m3u8`
     * manifest into an opaque blob whose relative segment URLs can no longer be resolved (broken
     * playback). Instead we return the NETWORK url and let the <video> element stream/range-request
     * it. Caching of video bytes happens elsewhere (SessionVideoCache / browser HTTP cache).
     */
    fun getVideoUrl(signedUrl: String): String = signedUrl

    fun invalidateImage(url: String) = imageCache.remove(S3CacheKeyResolver.resolve(url))
    suspend fun clearAll() = mutex.withLock {
        imageCache.clear()
    }

    // -----------------------------------------------------------------------
    // Internal helpers
    // -----------------------------------------------------------------------

    private suspend fun getOrFetchDeduped(
        key: String,
        fetchUrl: String,
        cache: LRUBlobCache
    ): String {
        // Decide what to do under the lock, but never await/suspend while holding it (the Mutex is
        // not reentrant). We either: hit the cache, join an existing in-flight fetch, or become the
        // owner of a new fetch.
        val existing: CompletableDeferred<String>?
        val owned: CompletableDeferred<String>?
        mutex.withLock {
            // Fast path: already cached. A just-fetched entry is protected from revocation by the
            // cache's min-age guard, so it's safe to hand out after releasing the lock.
            cache.get(key)?.let { return it }
            existing = inFlight[key]
            if (existing == null) {
                // Register ourselves as the owning fetch. While our key is in `inFlight` it is
                // "protected", so no concurrent put/evict can revoke the objectURL we create.
                owned = CompletableDeferred()
                inFlight[key] = owned
            } else {
                owned = null
            }
        }

        // Another fetch is already running for this key → await it (outside the lock).
        if (existing != null) return existing.await()

        // We own the fetch. The key stays protected (present in `inFlight`) until the finally block,
        // which only runs after the objectURL has been put into the cache and handed out.
        val deferred = owned!!
        return try {
            val objectUrl = fetchAndCache(key, fetchUrl, cache)
            deferred.complete(objectUrl)
            objectUrl
        } catch (e: Throwable) {
            deferred.completeExceptionally(e)
            throw e
        } finally {
            mutex.withLock { inFlight.remove(key) }
        }
    }

    private suspend fun fetchAndCache(
        key: String,
        url: String,
        cache: LRUBlobCache
    ): String {
        val response = window.fetch(url).await()
        val blob = awaitBlob(response.asDynamic())
        val sizeBytes: Long = (blob.asDynamic().size as Number).toLong()
        val objectUrl = JsURL.createObjectURL(blob)
        mutex.withLock { cache.put(key, sizeBytes, objectUrl) }
        return objectUrl
    }

    /** Calls response.blob() and awaits the resulting Promise. */
    private suspend fun awaitBlob(response: dynamic): dynamic = suspendCoroutine { cont ->
        val onFulfilled: (dynamic) -> Unit = { value -> cont.resume(value) }
        val onRejected: (dynamic) -> Unit = { err ->
            cont.resumeWithException(Throwable(err?.toString() ?: "blob() failed"))
        }
        js("(function(p,ok,err){p.then(ok,err);})")
            .unsafeCast<(dynamic, (dynamic) -> Unit, (dynamic) -> Unit) -> Unit>()(
                response.blob(), onFulfilled, onRejected
            )
    }
}
